import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

import AddNurseModal from '../components/AddNurseModal';
import ViewNurseModal from '../components/ViewNurseModal';
import EditNurseModal from '../components/EditNurseModal';
import ZoomableImage from '../components/ZoomableImage';

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- Interfaces ---
interface NurseContact { id: number; phone_number: string; }
interface NurseWhatsApp { id: number; whatsapp_number: string; }
interface NurseEmail { id: number; email: string; }
interface NurseQualification { id: number; qualification_name: string; institution_name?: string; }
interface NurseRegistration { id: number; council_name: string; register_number: string; }
interface NurseWorkExperience { id: number; institution_name: string; }
interface NurseLanguage { id: number; language: string; }

interface Nurse {
  id: number;
  full_name: string;
  dob: string;
  sex: string;
  profile: string;
  clinical_class: string;
  departments?: string;
  total_experience: string;
  staff_status: string;
  date_of_joining?: string;
  aadhar_number?: string;
  document_status?: string;
  native_state?: string;
  native_district?: string;
  religion?: string;
  marital_status?: string;
  contacts: NurseContact[];
  whatsapp_numbers: NurseWhatsApp[];
  emails: NurseEmail[];
  qualifications: NurseQualification[];
  registrations: NurseRegistration[];
  work_experiences: NurseWorkExperience[];
  languages: NurseLanguage[];
  photo_url?: string;
  monthly_salary?: number;
  designation?: string;
  [key: string]: any;
}

// --- FILTER OPTIONS ---
// Same vocabulary as Staff Pool / Daily Board (nurse_availability) — one status
// system used everywhere, instead of this screen keeping its own separate list.
// "On Duty" is derived (assigned today), not a pickable category, but is still
// filterable here since a nurse can genuinely be showing that status.
const availabilityOptions = ["Available","On Temporary Duty","Back-Up","On Duty - Shuffle Possibility","On Duty","On Leave - Regular","On Leave - Emergency","On Leave - Sick","Uncertainty","Resigned","Absconded"];
const documentStatusOptions = ["Full Set Documents","Not Available"];
const profileLevelOptions = ["Class 1 (Ventilator ICU Nurses)","Class 2 (Tracheostomy ICU Nurses)","Class 3 (Normal Nurses)","Class 4 (Junior Nurses)","Class 1 P (Neonatal & Paediatrics)"];
const gradeLevelOptions = ["Outstanding","High Profile","Upper Medium","Medium","Average","Low Profile","Below Average"];

// --- Icons ---
const AddIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>;
const UsersIcon = () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>;
const CheckCircleIcon = () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const ClockIcon = () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const XCircleIcon = () => <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const TickIcon = () => <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const CrossIcon = () => <svg width="15" height="15" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
const FilterIcon = () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>;
const SearchIcon = () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>;

const calculateAge = (dobString: string) => {
  if (!dobString) return '-';
  const today = new Date();
  const birthDate = new Date(dobString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

// tiny tick/cross
const TC = ({ v }: { v: boolean }) => (
  <div style={{ display:'flex', justifyContent:'center', alignItems:'center' }}>
    {v ? <TickIcon /> : <CrossIcon />}
  </div>
);

const NursesPage: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedNurseId, setSelectedNurseId] = useState<number | null>(null);
  const [nurses, setNurses] = useState<Nurse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [documentFilters, setDocumentFilters] = useState<string[]>([]);
  const [profileFilters, setProfileFilters] = useState<string[]>([]);
  const [gradeFilters, setGradeFilters] = useState<string[]>([]);
  const [registrationFilters, setRegistrationFilters] = useState<string[]>([]);

  const [stats, setStats] = useState({ total: 0, available: 0, offDuty: 0, resigned: 0 });
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Live status — same source Staff Pool / Daily Board read (nurse_availability),
  // not the old per-profile `staff_status` field. nurse_id -> effective status
  // for TODAY (real calendar date), "On Duty" applied on top for anyone with a
  // case assigned today, same precedence rule Staff Pool already uses.
  const [liveStatus, setLiveStatus] = useState<Record<number, string>>({});
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const fetchNurses = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('accessToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/nurses/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNurses(response.data.sort((a: Nurse, b: Nurse) => a.id - b.id));
    } catch { setError('Unable to load data.'); }
    finally { setLoading(false); }
  }, [API_BASE_URL]);

  const fetchLiveStatus = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const today = todayStr();
    try {
      const [availRes, asgnRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/nurse-availability`, { headers, params: { date: today } }),
        axios.get(`${API_BASE_URL}/api/assignments`, { headers, params: { start_date: today, end_date: today } }),
      ]);
      const onDutyIds = new Set(
        (asgnRes.data || []).flatMap((a: any) => [a.nurse?.id, a.second_nurse?.id]).filter(Boolean)
      );
      const map: Record<number, string> = {};
      (availRes.data || []).forEach((r: any) => { map[r.nurse_id] = onDutyIds.has(r.nurse_id) ? 'On Duty' : r.category; });
      onDutyIds.forEach((id: any) => { if (!map[id]) map[id] = 'On Duty'; }); // assigned but no availability row at all
      setLiveStatus(map);
    } catch { /* leave prior/empty map — nurses fall back to 'Available' below */ }
  }, [API_BASE_URL]);

  const refreshAll = useCallback(() => { fetchNurses(); fetchLiveStatus(); }, [fetchNurses, fetchLiveStatus]);

  useEffect(() => { fetchNurses(); fetchLiveStatus(); }, [fetchNurses, fetchLiveStatus]);

  useEffect(() => {
    let available = 0, offDuty = 0, resigned = 0;
    nurses.forEach(n => {
      const s = liveStatus[n.id] || 'Available';
      if (['Available', 'On Temporary Duty', 'Back-Up', 'On Duty - Shuffle Possibility', 'On Duty'].includes(s)) available++;
      else if (['On Leave - Regular', 'On Leave - Emergency', 'On Leave - Sick', 'Uncertainty'].includes(s)) offDuty++;
      else if (['Resigned', 'Absconded'].includes(s)) resigned++;
    });
    setStats({ total: nurses.length, available, offDuty, resigned });
  }, [nurses, liveStatus]);

  const addFilter = (list: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, v: string) => {
    if (v && !list.includes(v)) setter([...list, v]);
  };
  const removeFilter = (list: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, v: string) =>
    setter(list.filter(i => i !== v));
  const resetAllFilters = () => {
    setStatusFilters([]); setDocumentFilters([]); setProfileFilters([]);
    setGradeFilters([]); setRegistrationFilters([]); setSearchQuery('');
  };

  const filteredNurses = useMemo(() => nurses.filter(nurse => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q
      || nurse.full_name.toLowerCase().includes(q)
      || String(nurse.id).includes(q)
      || (nurse.aadhar_number || '').toLowerCase().includes(q)
      || (nurse.pan_number || '').toLowerCase().includes(q)
      || (nurse.passport_number || '').toLowerCase().includes(q)
      || nurse.contacts?.some(c => c.phone_number?.toLowerCase().includes(q))
      || nurse.whatsapp_numbers?.some(w => w.whatsapp_number?.toLowerCase().includes(q));
    const matchStatus = !statusFilters.length || statusFilters.includes(liveStatus[nurse.id] || 'Available');
    const matchDocs   = !documentFilters.length || documentFilters.includes(nurse.document_status || 'Not Available');
    const matchProf   = !profileFilters.length || profileFilters.includes(nurse.clinical_class || '');
    const matchGrade  = !gradeFilters.length || gradeFilters.includes(nurse.profile || '');
    let matchReg = true;
    if (registrationFilters.length) {
      const has = !!(nurse.registrations?.length);
      const wY = registrationFilters.includes('Yes'), wN = registrationFilters.includes('No');
      if (wY && wN) matchReg = true;
      else if (wY) matchReg = has;
      else if (wN) matchReg = !has;
    }
    return matchSearch && matchStatus && matchDocs && matchProf && matchGrade && matchReg;
  }).sort((a, b) => {
    const statusA = liveStatus[a.id] || 'Available';
    const statusB = liveStatus[b.id] || 'Available';
    const idxA = availabilityOptions.indexOf(statusA);
    const idxB = availabilityOptions.indexOf(statusB);
    return (idxA === -1 ? availabilityOptions.length : idxA) - (idxB === -1 ? availabilityOptions.length : idxB);
  }), [nurses, liveStatus, searchQuery, statusFilters, documentFilters, profileFilters, gradeFilters, registrationFilters]);

  const handleDeleteClick = async (id: number) => {
    if (!window.confirm('Are you sure? This action is irreversible.')) return;
    const token = localStorage.getItem('accessToken');
    try {
      await axios.delete(`${API_BASE_URL}/api/nurses/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      refreshAll();
    } catch { alert('Failed to delete'); }
  };

  const getImageUrl = (path?: string) =>
    path ? (path.startsWith('http') ? path : `${API_BASE_URL}/${path}`) : PLACEHOLDER_IMG;

  // Status vocabulary grouped into 4 colors: green (ready), blue (on duty),
  // orange (on leave), red (uncertain / not working here anymore).
  const STATUS_STYLE: Record<string, { backgroundColor: string; color: string }> = {
    'Available':                     { backgroundColor: '#dcfce7', color: '#15803d' },
    'On Temporary Duty':              { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    'Back-Up':                        { backgroundColor: '#dcfce7', color: '#15803d' },
    'On Duty - Shuffle Possibility':  { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    'On Duty':                        { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    'On Leave - Regular':             { backgroundColor: '#ffedd5', color: '#c2410c' },
    'On Leave - Emergency':           { backgroundColor: '#ffedd5', color: '#c2410c' },
    'On Leave - Sick':                { backgroundColor: '#ffedd5', color: '#c2410c' },
    'Uncertainty':                    { backgroundColor: '#fee2e2', color: '#b91c1c' },
    'Resigned':                       { backgroundColor: '#fee2e2', color: '#b91c1c' },
    'Absconded':                      { backgroundColor: '#fee2e2', color: '#b91c1c' },
  };
  const getStatusStyle = (status: string) => STATUS_STYLE[status] || { backgroundColor: '#f1f5f9', color: '#475569' };

  // Global CSS — injected once into the page
  const globalCss = `
    @keyframes marquee {
      0%   { transform: translateX(100%);  }
      100% { transform: translateX(-100%); }
    }
    .mq-wrap  { overflow:hidden; width:108px; }
    .mq-inner { display:inline-block; white-space:nowrap;
                animation: marquee 9s linear infinite; }
    .mq-inner:hover { animation-play-state: paused; }

    /* ── core compact table styles ── */
    .ctbl th, .ctbl td {
      padding: 4px 5px !important;
      font-size: 0.71rem !important;
      border: 1px solid #c5cbd3 !important;
      vertical-align: middle !important;
      white-space: nowrap;
    }
    .ctbl th {
      padding: 9px 7px !important;
      font-size: 0.72rem !important;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #1a3a5c;
      font-weight: 700;
      background: #86afd8 !important;
      text-align: center !important;
    }
    .ctbl thead { background: #86afd8 !important; }
    .ctbl { table-layout: fixed; width: 100% !important; }
    .ctbl td.wc { white-space: normal !important; }
  `;

  // FilterSection component
  const FilterSection = ({
    label, options, selected, setSelected
  }: {
    label: string; options: string[];
    selected: string[]; setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }) => (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', minWidth:'155px' }}>
      <label style={{ fontSize:'0.67rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>
        {label}
      </label>
      <select
        style={{ padding:'0.38rem 0.5rem', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'0.76rem', color:'#334155', outline:'none', cursor:'pointer', backgroundColor:'#fff' }}
        value=""
        onChange={e => addFilter(selected, setSelected, e.target.value)}
      >
        <option value="" disabled>Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem' }}>
        {selected.map(item => (
          <span key={item} style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', backgroundColor:'#eff6ff', color:'#1d4ed8', fontSize:'0.67rem', fontWeight:600, padding:'1px 6px', borderRadius:'999px', border:'1px solid #bfdbfe' }}>
            {item}
            <span
              onClick={() => removeFilter(selected, setSelected, item)}
              style={{ cursor:'pointer', width:'11px', height:'11px', borderRadius:'50%', backgroundColor:'#bfdbfe', color:'#1e40af', fontSize:'8px', display:'flex', alignItems:'center', justifyContent:'center' }}
            >✕</span>
          </span>
        ))}
      </div>
    </div>
  );

  const renderContacts = (nurse: Nurse) => {
    const nums = Array.from(new Set([
      ...(nurse.contacts?.map(c => c.phone_number).filter(Boolean) || []),
      ...(nurse.whatsapp_numbers?.map(w => w.whatsapp_number).filter(Boolean) || [])
    ]));
    if (!nums.length) return <span>-</span>;
    return <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>{nums.map((n,i)=><span key={i}>{n}</span>)}</div>;
  };

  const renderQualifications = (qs: NurseQualification[]) => {
    if (!qs?.length) return <span>-</span>;
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
        {qs.map((q, i) => (
          <div key={i} style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
            <span style={{ fontWeight:600 }}>{q.qualification_name}</span>
            {q.institution_name && (
              q.institution_name.length > 14
                ? <div className="mq-wrap"><span className="mq-inner" style={{ color:'#64748b', fontSize:'0.67rem' }}>{q.institution_name}</span></div>
                : <span style={{ color:'#64748b', fontSize:'0.67rem' }}>{q.institution_name}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding:'1rem', backgroundColor:'#f8fafc', minHeight:'100vh', fontFamily:'"Inter", sans-serif' }}>
      <style>{globalCss}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <div>
          <h1 style={{ margin:0, fontSize:'1.35rem', fontWeight:700, color:'#0f172a' }}>Nurse Management</h1>
          <p style={{ margin:0, color:'#64748b', fontSize:'0.76rem' }}>Overview of all nursing staff, availability, and profiles.</p>
        </div>
        <button
          style={{ backgroundColor:'#0f172a', color:'white', border:'none', borderRadius:'7px', padding:'0.5rem 0.9rem', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.35rem' }}
          onClick={() => setIsAddModalOpen(true)}
        >
          <AddIcon /> Add New Nurse
        </button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.75rem', marginBottom:'1rem' }}>
        {[
          { icon:<UsersIcon/>,      bg:'#eff6ff', color:'#3b82f6', val:stats.total,     label:'Total Staff' },
          { icon:<CheckCircleIcon/>,bg:'#f0fdf4', color:'#22c55e', val:stats.available, label:'Available'   },
          { icon:<ClockIcon/>,      bg:'#fff7ed', color:'#f97316', val:stats.offDuty,   label:'Off Duty'    },
          { icon:<XCircleIcon/>,    bg:'#fef2f2', color:'#ef4444', val:stats.resigned,  label:'Resigned'    },
        ].map(({ icon, bg, color, val, label }) => (
          <div key={label} style={{ backgroundColor:'white', padding:'0.75rem 1rem', borderRadius:'9px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:'0.65rem' }}>
            <div style={{ width:'34px', height:'34px', borderRadius:'7px', backgroundColor:bg, color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</div>
            <div>
              <div style={{ fontSize:'1.3rem', fontWeight:700, color:'#1e293b', lineHeight:1 }}>{loading ? '-' : val}</div>
              <div style={{ fontSize:'0.72rem', color:'#64748b', marginTop:'2px' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'0.85rem', alignItems:'flex-start', backgroundColor:'white', padding:'0.85rem', borderRadius:'9px', border:'1px solid #e2e8f0', marginBottom:'1rem', boxShadow:'0 2px 4px rgba(0,0,0,0.03)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.35rem', minWidth:'100%', paddingBottom:'0.4rem', borderBottom:'1px solid #f1f5f9' }}>
          <FilterIcon/>
          <span style={{ fontWeight:700, fontSize:'0.76rem', color:'#0f172a' }}>MULTI-FILTERS</span>
        </div>

        <FilterSection label="Staff Status"    options={availabilityOptions}   selected={statusFilters}       setSelected={setStatusFilters}/>
        <FilterSection label="Doc Status"      options={documentStatusOptions} selected={documentFilters}     setSelected={setDocumentFilters}/>
        <FilterSection label="Profile Level"   options={profileLevelOptions}   selected={profileFilters}      setSelected={setProfileFilters}/>
        <FilterSection label="Grade Level"     options={gradeLevelOptions}     selected={gradeFilters}        setSelected={setGradeFilters}/>
        <FilterSection label="Registration"    options={['Yes','No']}          selected={registrationFilters} setSelected={setRegistrationFilters}/>

        {/* Search */}
        <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', marginLeft:'auto' }}>
          <label style={{ fontSize:'0.67rem', fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>Search</label>
          <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
            <div style={{ position:'absolute', left:'7px', color:'#94a3b8', display:'flex' }}><SearchIcon/></div>
            <input
              type="text"
              placeholder="Name / ID / Aadhar / Phone…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ paddingLeft:'1.75rem', paddingRight:'0.55rem', paddingTop:'0.38rem', paddingBottom:'0.38rem', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'0.76rem', color:'#334155', outline:'none', width:'240px' }}
            />
          </div>
        </div>

        {(statusFilters.length || documentFilters.length || profileFilters.length || gradeFilters.length || registrationFilters.length || searchQuery) ? (
          <button onClick={resetAllFilters} style={{ alignSelf:'flex-end', border:'none', background:'#fef2f2', color:'#ef4444', fontSize:'0.72rem', fontWeight:600, cursor:'pointer', padding:'0.38rem 0.85rem', borderRadius:'6px' }}>
            Clear All
          </button>
        ) : null}
      </div>

      {loading && <div style={{ textAlign:'center', padding:'4rem', color:'#64748b' }}>Loading staff data…</div>}

      {/* Table */}
      {!loading && !error && (
        <div style={{ backgroundColor:'white', borderRadius:'9px', boxShadow:'0 4px 6px -1px rgba(0,0,0,0.05)', border:'1px solid #e2e8f0', overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table className="ctbl" style={{ width:'100%', borderCollapse:'collapse', textAlign:'left', color:'#1d2631' }}>
              <thead>
                <tr>
                  <th style={{ width:'30px', textAlign:'center' }}>Sl.No</th>
                  <th style={{ width:'60px', textAlign:'center' }}>Photo</th>
                  <th style={{ width:'105px', textAlign:'center' }}>Name</th>
                  <th style={{ width:'50px', textAlign:'center' }}>Age/Sex</th>
                  <th style={{ width:'118px' }}>Qualification</th>
                  <th style={{ width:'30px', textAlign:'center' }}>Reg</th>
                  <th style={{ width:'52px' }}>Exp</th>
                  <th style={{ width:'76px' }}>Dept</th>
                  <th style={{ width:'105px' }}>Hospital(s)</th>
                  <th style={{ width:'88px'  }}>Languages</th>
                  <th style={{ width:'96px'  }}>Relig / Marital</th>
                  <th style={{ width:'88px'  }}>State / Dist</th>
                  <th style={{ width:'30px', textAlign:'center' }}>A</th>
                  <th style={{ width:'26px', textAlign:'center' }}>D</th>
                  <th style={{ width:'115px' }}>Contact</th>
                  <th style={{ width:'58px', textAlign:'center' }}>Status</th>
                  <th style={{ width:'68px', textAlign:'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNurses.length === 0 ? (
                  <tr>
                    <td colSpan={17} style={{ padding:'2rem', textAlign:'center', color:'#64748b' }}>
                      No nurses found matching the selected filters.
                    </td>
                  </tr>
                ) : filteredNurses.map((nurse, index) => (
                  <tr
                    key={nurse.id}
                    style={{ borderBottom:'1px solid #f1f5f9', backgroundColor: hoverRowId === nurse.id ? '#f8fafc' : 'white', transition:'background 0.1s' }}
                    onMouseEnter={() => setHoverRowId(nurse.id)}
                    onMouseLeave={() => setHoverRowId(null)}
                  >
                    {/* # */}
                    <td style={{ textAlign:'center', color:'#94a3b8', fontWeight:600 }}>{index + 1}</td>

                    {/* Photo */}
                    <td style={{ textAlign:'center' }}>
                      <ZoomableImage
                        src={getImageUrl(nurse.photo_url)}
                        alt={`${nurse.full_name}'s photo`}
                        style={{ width:'50px', height:'50px', borderRadius:'5px', objectFit:'cover', border:'1px solid #e2e8f0', display:'block', margin:'0 auto' }}
                        fallbackSrc={PLACEHOLDER_IMG}
                      />
                    </td>

                    {/* Name */}
                    <td className="wc" style={{ fontWeight:600, color:'#0f172a', lineHeight:1.3 }}>
                      {nurse.full_name}
                    </td>

                    {/* Age / Sex */}
                    <td style={{ textAlign:'center', whiteSpace:'nowrap' }}>
                      {calculateAge(nurse.dob)}/{nurse.sex?.[0] ?? '-'}
                    </td>

                    {/* Qualification */}
                    <td className="wc">{renderQualifications(nurse.qualifications)}</td>

                    {/* Reg */}
                    <td style={{ textAlign:'center' }}>
                      <TC v={!!(nurse.registrations?.length)} />
                    </td>

                    {/* Experience */}
                    <td>{nurse.total_experience || '-'}</td>

                    {/* Department */}
                    <td className="wc">{nurse.departments || '-'}</td>

                    {/* Hospitals */}
                    <td className="wc">
                      {nurse.work_experiences?.length
                        ? <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
                            {nurse.work_experiences.map((w,i) => <span key={i}>{w.institution_name}</span>)}
                          </div>
                        : '-'}
                    </td>

                    {/* Languages */}
                    <td className="wc">
                      {nurse.languages?.length ? nurse.languages.map(l => l.language).join(', ') : '-'}
                    </td>

                    {/* Religion / Marital */}
                    <td className="wc">
                      <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
                        <span>{nurse.religion || '-'}</span>
                        <span style={{ color:'#64748b', fontSize:'0.64rem', backgroundColor:'#f1f5f9', padding:'1px 4px', borderRadius:'999px', display:'inline-block', width:'fit-content' }}>
                          {nurse.marital_status || '–'}
                        </span>
                      </div>
                    </td>

                    {/* State / District */}
                    <td className="wc">
                      <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
                        <span style={{ fontWeight:600 }}>{nurse.native_state || '-'}</span>
                        <span style={{ color:'#64748b', fontSize:'0.64rem' }}>{nurse.native_district || ''}</span>
                      </div>
                    </td>

                    {/* Aadhar */}
                    <td style={{ textAlign:'center' }}><TC v={!!nurse.aadhar_number} /></td>

                    {/* D — Documents */}
                    <td style={{ textAlign:'center' }}>
                      <TC v={nurse.document_status === 'Full Set Documents'} />
                    </td>

                    {/* Contact */}
                    <td style={{ whiteSpace:'nowrap' }}>{renderContacts(nurse)}</td>

                    {/* Status — live, from nurse_availability (same as Staff Pool), not the
                        dormant profile-level staff_status field. */}
                    <td style={{ textAlign:'center' }}>
                      {(() => {
                        const abbrev: Record<string,string> = {
                          'Available':                    'Available',
                          'On Temporary Duty':             'Temp Duty',
                          'Back-Up':                       'Back-Up',
                          'On Duty - Shuffle Possibility':  'Shuffle Poss.',
                          'On Duty':                       'On Duty',
                          'On Leave - Regular':            'Leave (Reg)',
                          'On Leave - Emergency':          'Leave (Emg)',
                          'On Leave - Sick':                'Leave (Sick)',
                          'Uncertainty':                   'Uncertain',
                          'Resigned':                      'Resigned',
                          'Absconded':                     'Absconded',
                        };
                        const status = liveStatus[nurse.id] || 'Available';
                        const label = abbrev[status] ?? status;
                        return (
                          <span style={{ ...getStatusStyle(status), display:'inline-flex', alignItems:'center', padding:'2px 4px', borderRadius:'999px', fontSize:'0.60rem', fontWeight:600, lineHeight:1, whiteSpace:'nowrap' }}>
                            {label}
                          </span>
                        );
                      })()}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign:'center' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:'3px', alignItems:'center' }}>
                        {[
                          { label:'View',   bg:'linear-gradient(135deg,#6366f1,#4f46e5)', fn:() => { setSelectedNurseId(nurse.id); setIsDetailModalOpen(true); } },
                          { label:'Edit',   bg:'linear-gradient(135deg,#0ea5e9,#2563eb)', fn:() => { setSelectedNurseId(nurse.id); setIsEditModalOpen(true); } },
                          { label:'Delete', bg:'linear-gradient(135deg,#f43f5e,#e11d48)', fn:() => handleDeleteClick(nurse.id) },
                        ].map(({ label, bg, fn }) => (
                          <button key={label} onClick={fn} style={{ background:bg, color:'white', border:'none', borderRadius:'4px', padding:'3px 0', fontSize:'0.66rem', fontWeight:600, cursor:'pointer', width:'54px' }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <AddNurseModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSuccess={() => { setIsAddModalOpen(false); refreshAll(); }}/>
      )}
      {isDetailModalOpen && (
        <ViewNurseModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} nurseId={selectedNurseId}/>
      )}
      {isEditModalOpen && selectedNurseId && (
        <EditNurseModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => { setIsEditModalOpen(false); refreshAll(); }}
          nurseId={selectedNurseId}
        />
      )}
    </div>
  );
};

export default NursesPage;